import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2, TrendingUp, AlertTriangle, BarChart3, Plus, Activity, ChevronRight } from 'lucide-react'
import { PageHeader, StatCard, EmptyState, Button } from '@/components/ui'

interface BusinessSummary {
  id: string
  name: string
  businessType?: string
  stage: string
  currency: string
  createdAt: string
}

const STAGE_LABELS: Record<string, { label: string; color: string }> = {
  planning: { label: 'Perencanaan', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  running:  { label: 'Berjalan',   color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  scaling:  { label: 'Scaling',    color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
  paused:   { label: 'Ditangguhkan', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  closed:   { label: 'Ditutup',    color: 'text-rose-400 bg-rose-500/10 border-rose-500/20' },
}

export function GlobalDashboardPage() {
  const navigate = useNavigate()
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([])
  const [isLoading, setIsLoading]   = useState(true)

  useEffect(() => {
    fetch('/api/businesses')
      .then(r => r.json())
      .then((json: any) => {
        if (json.success) setBusinesses(json.data)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [])

  const running  = businesses.filter(b => b.stage === 'running' || b.stage === 'scaling')
  const planning = businesses.filter(b => b.stage === 'planning')
  const closed   = businesses.filter(b => b.stage === 'closed' || b.stage === 'paused')

  return (
    <div className="page-container">
      <PageHeader
        title="Dashboard"
        description="Ikhtisar kondisi seluruh bisnis Anda dalam satu pandangan."
        action={
          <Button
            id="dashboard-add-business"
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => navigate('/businesses/new')}
          >
            Bisnis Baru
          </Button>
        }
      />

      {/* Portfolio Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Bisnis"     value={String(businesses.length)} icon={Building2}   subtitle="Semua status" />
        <StatCard title="Aktif / Scaling"  value={String(running.length)}    icon={Activity}    subtitle="Berjalan & growing" />
        <StatCard title="Perencanaan"      value={String(planning.length)}   icon={BarChart3}   subtitle="Belum dimulai" />
        <StatCard title="Tutup / Paused"   value={String(closed.length)}     icon={AlertTriangle} subtitle="Perlu perhatian" />
      </div>

      {isLoading ? (
        <div className="text-slate-500 text-center py-16">Memuat bisnis Anda...</div>
      ) : businesses.length === 0 ? (
        <>
          <EmptyState
            icon={Building2}
            title="Belum ada bisnis"
            description="Tambahkan bisnis pertama Anda untuk mulai melacak performa keuangan, simulasi, dan kondisi bisnis."
            action={{ label: 'Buat Bisnis Pertama', onClick: () => navigate('/businesses/new') }}
            size="lg"
          />

          {/* Getting started guide */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Cara Memulai</h3>
            <ol className="space-y-2">
              {[
                { step: '1', label: 'Buat profil bisnis', desc: 'Nama, jenis usaha, tanggal mulai' },
                { step: '2', label: 'Input modal & founder', desc: 'Siapa yang berkontribusi dan berapa' },
                { step: '3', label: 'Tambah produk & biaya', desc: 'HPP, harga jual, biaya operasional' },
                { step: '4', label: 'Jalankan simulasi', desc: 'Lihat proyeksi BEP, cash runway, dan ekuitas' },
              ].map(({ step, label, desc }) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-[10px] font-bold text-brand-400 mt-0.5">
                    {step}
                  </span>
                  <div>
                    <p className="text-xs font-medium text-slate-300">{label}</p>
                    <p className="text-[11px] text-slate-500">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-4">
              <Link to="/businesses/new">
                <Button id="dashboard-start-cta" size="sm" variant="outline">Mulai Sekarang</Button>
              </Link>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Business Cards Grid */}
          <div>
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Bisnis Anda</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {businesses.map(biz => {
                const stageInfo = STAGE_LABELS[biz.stage] ?? { label: biz.stage, color: 'text-slate-400 bg-slate-700/50 border-slate-600' }
                return (
                  <Link
                    key={biz.id}
                    to={`/businesses/${biz.id}/dashboard`}
                    className="group block bg-slate-900 border border-slate-800 hover:border-brand-500/40 rounded-xl p-5 transition-all hover:shadow-lg hover:shadow-brand-500/5"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                          <Building2 size={16} className="text-brand-400" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">{biz.name}</h3>
                          <p className="text-xs text-slate-500">{biz.businessType || 'Bisnis Umum'}</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-600 group-hover:text-brand-400 transition-colors mt-0.5" />
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${stageInfo.color}`}>
                        {stageInfo.label}
                      </span>
                      <span className="text-[10px] text-slate-600">
                        Dibuat {new Date(biz.createdAt).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-3 gap-2">
                      {[
                        { label: 'Simulasi', href: `/businesses/${biz.id}/simulations` },
                        { label: 'Dashboard', href: `/businesses/${biz.id}/dashboard` },
                        { label: 'Laporan',  href: `/businesses/${biz.id}/reports` },
                      ].map(link => (
                        <Link
                          key={link.label}
                          to={link.href}
                          onClick={e => e.stopPropagation()}
                          className="text-center text-[10px] text-slate-500 hover:text-brand-400 py-1 px-2 rounded hover:bg-brand-500/5 transition-colors"
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </Link>
                )
              })}

              {/* Add Business Card */}
              <button
                onClick={() => navigate('/businesses/new')}
                className="flex flex-col items-center justify-center gap-2 bg-slate-900/50 border border-dashed border-slate-700 hover:border-brand-500/40 rounded-xl p-5 transition-all text-slate-500 hover:text-brand-400 min-h-[140px]"
              >
                <Plus size={20} />
                <span className="text-xs font-medium">Tambah Bisnis Baru</span>
              </button>
            </div>
          </div>

          {/* Quick tip */}
          <div className="rounded-xl border border-brand-500/20 bg-brand-500/5 p-4">
            <div className="flex gap-3">
              <TrendingUp size={16} className="text-brand-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-brand-300">Tips</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Buka setiap bisnis → Dashboard untuk melihat health score, decision signal, BEP, NPV, dan proyeksi ekuitas.
                  Gunakan tab Simulasi untuk membandingkan skenario conservative, moderate, dan optimistic.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
